// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { useState, useEffect, useMemo } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  ButtonGroup,
  Progress,
  Form,
  Label,
  Input,
  Spinner
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import StorageDashboard from "../../storage";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import {
  CODE_TRUNCATE_FULL_ROW,
  SUITE_PROGRESS_HEIGHT_6
} from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import {
  AsyncStateBoundary,
  SuitePageShell,
  SuiteListToolbar,
  MockPaginationFooter
} from "../../_suite/primitives";

const FILE_PAGE = 6;

function FileBrowserPage() {
  const [mode, setMode] = useState("grid");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.storage.listFiles(),
    []
  );

  const filtered = useMemo(() => {
    const list = data || [];
    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter(
      f =>
        String(f.name).toLowerCase().includes(term) ||
        String(f.folder).toLowerCase().includes(term)
    );
  }, [data, q]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * FILE_PAGE;
    return filtered.slice(start, start + FILE_PAGE);
  }, [filtered, page]);

  return (
    <SuitePageShell
      heading={<IntlMessages id="suite.storage.files" />}
      afterBreadcrumb={
        <div className="d-flex align-items-center gap-2 mb-0 flex-wrap">
          <ButtonGroup size="sm">
            <Button
              color={mode === "grid" ? "primary" : "light"}
              onClick={() => setMode("grid")}
            >
              Grid
            </Button>
            <Button
              color={mode === "list" ? "primary" : "light"}
              onClick={() => setMode("list")}
            >
              List
            </Button>
          </ButtonGroup>
        </div>
      }
    >
      <SuiteListToolbar
        searchValue={q}
        onSearchChange={e => {
          setQ(e.target.value);
          setPage(1);
        }}
        searchPlaceholder="Search file name or folder…"
      >
        <span />
      </SuiteListToolbar>
      <AsyncStateBoundary loading={loading} error={error}>
        {mode === "grid" ? (
          <Row>
            {pageRows.map(f => (
              <Col md="6" xl="4" key={f.id} className="mb-3">
                <Card className="h-100 border-0 shadow-sm">
                  <CardBody>
                    <i className="simple-icon-folder-alt h2 text-warning d-block mb-2" />
                    <h6>{f.name}</h6>
                    <p className="small text-muted mb-2">{f.folder}</p>
                    <p className="small mb-2">{f.sizeMb} MB · {f.modified}</p>
                    <Button
                      tag={Link}
                      size="sm"
                      color="primary"
                      to={f.id}
                      relative="path"
                    >
                      Open
                    </Button>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardBody className="p-0">
              <table className="table table-hover mb-0">
                <tbody>
                  {pageRows.map(f => (
                    <tr key={f.id}>
                      <td>
                        <Link to={f.id} relative="path">
                          {f.name}
                        </Link>
                      </td>
                      <td className="text-muted">{f.folder}</td>
                      <td>
                        {f.sizeMb} MB {f.version && <small>({f.version})</small>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        )}
        <MockPaginationFooter
          page={page}
          pageSize={FILE_PAGE}
          total={filtered.length}
          onPageChange={setPage}
        />
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function FileDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.storage.getFile(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.storage.file-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Row>
            <Col md="8">
              <Card>
                <CardBody>
                  <h3>{data.name}</h3>
                  <p className="text-muted">{data.folder}</p>
                  <p>Version history and preview would render from your blob API.</p>
                </CardBody>
              </Card>
            </Col>
            <Col md="4">
              <Card className="bg-light">
                <CardBody>
                  <CardTitle tag="h6">Metadata</CardTitle>
                  <p className="small mb-1">Size: {data.sizeMb} MB</p>
                  <p className="small mb-0">Modified: {data.modified}</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function SharesExpiryPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.storage.listShares(),
    []
  );
  const [copied, setCopied] = useState(null);
  return (
    <SuitePageShell
      heading={<IntlMessages id="suite.storage.shares" />}
      afterBreadcrumb={
        <p className="text-muted small mb-0">
          <IntlMessages id="suite.storage.shares-hint" />
        </p>
      }
    >
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(s => (
          <Card key={s.id} className="mb-3 border-0 shadow-sm">
            <CardBody>
              <div className="d-flex justify-content-between mb-2 flex-wrap gap-2">
                <strong>{s.resource}</strong>
                <Badge>{s.permissions}</Badge>
              </div>
              <div className="small text-muted mb-2">
                Expires {s.expires}
                {s.views != null && (
                  <span className="ms-2">
                    · {s.views} views · last {s.lastAccessed}
                  </span>
                )}
              </div>
              {s.shareUrl && (
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <code className="small bg-light px-2 py-1 rounded text-truncate d-inline-block" style={CODE_TRUNCATE_FULL_ROW}>
                    {s.shareUrl}
                  </code>
                  <Button
                    size="sm"
                    color="primary"
                    outline
                    onClick={() => {
                      navigator.clipboard?.writeText(s.shareUrl);
                      setCopied(s.id);
                      setTimeout(() => setCopied(null), 2000);
                    }}
                  >
                    {copied === s.id ? "Copied" : "Copy link"}
                  </Button>
                </div>
              )}
              <Progress value={72} className="mb-0" style={SUITE_PROGRESS_HEIGHT_6} />
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function StorageQuotaSettingsPage() {
  const { data, loading, reload } = useAsyncResource(
    () => suiteApi.storage.getStorageSettings(),
    []
  );
  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm();

  useEffect(() => {
    if (data) reset(data);
  }, [data, reset]);

  async function onSubmit(v) {
    await suiteApi.storage.saveStorageSettings(v);
    reload();
  }

  const quota = Number(data?.quotaGb) || 500;
  const used = 312;

  return (
    <SuitePageShell heading={<IntlMessages id="suite.storage.settings" />}>
      {loading && !data && <Spinner />}
      {data && (
        <Row>
          <Col md="6">
            <Card>
              <CardBody>
                <CardTitle tag="h5">Quotas</CardTitle>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-3">
                    <Label>
                      <IntlMessages id="suite.storage.quota" />
                    </Label>
                    <Input type="number" {...register("quotaGb")} />
                  </div>
                  <div className="mb-3">
                    <Label>
                      <IntlMessages id="suite.storage.retention" />
                    </Label>
                    <Input type="number" {...register("retentionDays")} />
                  </div>
                  <Button color="primary" type="submit" disabled={isSubmitting}>
                    Save
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
          <Col md="6">
            <Card className="bg-dark text-white">
              <CardBody>
                <CardTitle className="text-white">Usage meter</CardTitle>
                <p className="small text-white-50">
                  {used} GB of {quota} GB used (demo)
                </p>
                <Progress
                  value={Math.min(100, (used / quota) * 100)}
                  color="info"
                  className="mb-0"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </SuitePageShell>
  );
}

export function StorageSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="storage" />}>
        <Route index element={<StorageDashboard />} />
        <Route path="files" element={<FileBrowserPage />} />
        <Route path="files/:id" element={<FileDetailPage />} />
        <Route path="shares" element={<SharesExpiryPage />} />
        <Route path="settings" element={<StorageQuotaSettingsPage />} />
      </Route>
    </Routes>
  );
}
